import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import useOdulOneriCall from '../../hooks/useOdulOneriCall'
import {
  clearTrackingSession,
  readOpexSession,
  readTrackingSession,
} from '../../helper/odulOneriTrackingSession'
import {
  APP_STATE,
  APP_STATE_OPTIONS,
  RECORD_STATUS,
  RECORD_STATUS_LABELS,
  deriveRecordStatusFromAppState,
  displayAppStateLabel,
  getAppStateLabel,
  getRecordStatusLabel,
  resolveAppState,
} from '../../helper/odulOneriStatus'
import {
  ACTUAL_BENEFIT_AREAS,
  ACHIEVEMENT_OPTIONS,
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_FILES,
  OPEX_VERIFIER_UNITS,
  OPEX_VERIFY_METHODS,
  OPEX_VERIFY_PERIODS,
  VERIFICATION_CHOICE,
  VERIFICATION_CHOICE_LABELS,
  amountInputToNumberOrNull,
  formatDateDMY,
  formatEur,
  formatFileSize,
  getImplementationMethodLabel,
  normalizeResultAttachments,
  validateAttachment,
} from '../../helper/odulOneriImplementation'
import MoneyInput from './MoneyInput'
import {
  formatHistoryWhen,
  getHistoryTypeLabel,
} from '../../helper/odulOneriHistory'
import {
  formatWeightedScore,
  resolveBoardGroupScore,
} from '../../helper/odulOneriScoring'
import { isOdulOneriOpexAuthorized } from '../../helper/authorizedUsers'
import { REPORT_KEYS } from '../../helper/accessControlConstants'
import './OpexUygulamaTakip.css'

const statusPillClass = (statusOrLabel) => {
  const s = String(statusOrLabel || '')
  if (s.includes('Gecik') || s === RECORD_STATUS.IMPL_DELAYED) return 'pill late'
  if (
    s === RECORD_STATUS.AWARD_POOL ||
    s === RECORD_STATUS.CLOSED_AWARDED ||
    s === RECORD_STATUS.CLOSED_THANKS ||
    s.includes('Kapandı') ||
    s.includes('Tamamlandı')
  ) {
    return 'pill ok'
  }
  if (
    s === RECORD_STATUS.IMPL_RESULT_PENDING ||
    s === RECORD_STATUS.IMPL_CORRECTION ||
    s.includes('Doğrulama') ||
    s.includes('Bekleniyor')
  ) {
    return 'pill wait'
  }
  return 'pill'
}

const normalizeEmail = (value) => String(value || '').trim().toLowerCase()

const isoToday = () =>
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date())

const emptyLeaderOutcome = {
  leaderResult: '',
  leaderKpi: '',
  leaderNumeric: '',
  leaderQualitative: '',
  leaderAchievement: '',
  leaderEvidence: '',
  leaderCost: '',
  leaderGain: '',
}

const OpexUygulamaTakip = () => {
  const { currentUser } = useSelector((state) => state.auth)
  const permissions = useSelector((state) => state.accessControl.permissions)
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const deepImplId = String(searchParams.get('implementationId') || '').trim()
  const accessToken = String(searchParams.get('access') || '').trim()

  const {
    fetchImplementations,
    fetchAllOdulOneriRecords,
    updateImplementationTracking,
    verifyImplementation,
    uploadResultAttachments,
    fetchRecordHistory,
    requestTrackingAccess,
    claimTrackingAccess,
  } = useOdulOneriCall()

  const firebaseEmail = normalizeEmail(currentUser)
  const userReports = permissions[firebaseEmail]?.reports
  const isFirebaseOpex =
    Boolean(userReports?.[REPORT_KEYS.ODUL_OPEX]) || isOdulOneriOpexAuthorized(currentUser)

  const [emailSession, setEmailSession] = useState(() => readTrackingSession())
  const [opexEmailSession] = useState(() => readOpexSession())
  const [claimingAccess, setClaimingAccess] = useState(Boolean(accessToken))
  const [gateEmail, setGateEmail] = useState('')
  const [gateBusy, setGateBusy] = useState(false)
  const [gateMessage, setGateMessage] = useState('')

  const sessionEmail = normalizeEmail(emailSession?.email)
  const opexSessionEmail = normalizeEmail(opexEmailSession?.email)
  const isOpexUser = Boolean(isFirebaseOpex || opexSessionEmail)
  const userEmail = firebaseEmail || opexSessionEmail || sessionEmail
  const hasAccess = Boolean(isOpexUser || userEmail)
  const accessViaEmailSession = Boolean((sessionEmail || opexSessionEmail) && !firebaseEmail)

  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [selectedId, setSelectedId] = useState('')
  const [detailTab, setDetailTab] = useState('actions')
  const [historyRows, setHistoryRows] = useState([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [proposalOpen, setProposalOpen] = useState(false)

  const [appState, setAppState] = useState(APP_STATE.NOT_STARTED)
  const [statusNote, setStatusNote] = useState('')
  const [completionDate, setCompletionDate] = useState('')
  const [outcome, setOutcome] = useState(emptyLeaderOutcome)
  const [verifyForm, setVerifyForm] = useState({
    verificationChoice: VERIFICATION_CHOICE.SAME,
    revisionReason: '',
    actualBenefitArea: '',
    verifier: 'OPEX',
    verifyOwner: '',
    verifyMethod: '',
    verifyPeriod: '',
    verifyDate: '',
    evidence: '',
    note: '',
    verifiedKpi: '',
    verifiedNumeric: '',
    verifiedQualitative: '',
    verifyAchievement: '',
    actualCost: '',
    verifiedGain: '',
  })
  /** Kaydedilmiş OPEX sonuç ekleri + henüz yüklenmemiş dosyalar */
  const [savedResultAttachments, setSavedResultAttachments] = useState([])
  const [pendingResultFiles, setPendingResultFiles] = useState([])
  const [attachmentError, setAttachmentError] = useState('')
  const [saving, setSaving] = useState(false)
  const [clientError, setClientError] = useState('')
  const [facilityFilter, setFacilityFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    if (!hasAccess) {
      setRows([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [impls, records] = await Promise.all([
        fetchImplementations(),
        fetchAllOdulOneriRecords(),
      ])
      const recordById = Object.fromEntries(records.map((r) => [r.id, r]))
      const mapped = impls.map((impl) => ({
        ...impl,
        record: recordById[impl.recordId] || null,
      }))
      setRows(mapped)
      setSelectedId((prev) => {
        if (deepImplId && mapped.some((r) => r.id === deepImplId)) return deepImplId
        if (prev && mapped.some((r) => r.id === prev)) return prev
        return mapped[0]?.id || ''
      })
    } finally {
      setLoading(false)
    }
  }, [fetchImplementations, fetchAllOdulOneriRecords, deepImplId, hasAccess])

  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!accessToken) {
        setClaimingAccess(false)
        return
      }
      setClaimingAccess(true)
      const result = await claimTrackingAccess(accessToken)
      if (!mounted) return
      if (result.ok) {
        setEmailSession(result.session || readTrackingSession())
        const next = new URLSearchParams(searchParams)
        next.delete('access')
        if (result.implementationId) next.set('implementationId', result.implementationId)
        setSearchParams(next, { replace: true })
      } else {
        setGateMessage(
          result.error === 'expired'
            ? 'Bağlantının süresi dolmuş. E-posta ile yeniden giriş isteyin.'
            : result.error === 'used'
              ? 'Bu bağlantı daha önce kullanılmış. E-posta ile yeniden giriş isteyin.'
              : 'Giriş bağlantısı geçersiz.'
        )
        const next = new URLSearchParams(searchParams)
        next.delete('access')
        setSearchParams(next, { replace: true })
      }
      setClaimingAccess(false)
    })()
    return () => {
      mounted = false
    }
    // accessToken değişince bir kez claim
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken])

  useEffect(() => {
    if (claimingAccess) return
    load()
  }, [load, claimingAccess])

  const tableRows = useMemo(
    () =>
      rows.map((row) => {
        const resolved = resolveAppState(row, row.record)
        const due = row.plan?.dueDate || ''
        return {
          id: row.id,
          recordNumber: row.recordNumber || row.record?.recordNumber || '—',
          uygulamaNo: row.uygulamaNo || '—',
          facility: row.facility || row.record?.applicant?.location || '—',
          department: row.record?.relatedDepartmentName || row.relatedDepartmentName || '—',
          topic: row.topic || row.record?.topic || '—',
          owner: row.record?.applicant?.fullName || row.applicantName || '—',
          uygulamaSahibi: row.plan?.ownerName || row.record?.applicant?.fullName || '—',
          leader: row.plan?.leaderName || '—',
          due: formatDateDMY(due),
          dueRaw: due,
          method: getImplementationMethodLabel(row.plan?.method || row.method),
          summary: row.plan?.workSummary || '—',
          appState: resolved,
          appStateLabel: displayAppStateLabel(resolved, due),
          status: row.record?.status,
          statusLabel: getRecordStatusLabel(row.record?.status),
          actualBenefitArea: row.realization?.actualBenefitArea || '—',
          opexVerify:
            row.opexVerification?.result === 'verified'
              ? 'Doğrulandı'
              : row.opexVerification?.result === 'returned'
                ? 'Düzeltme bekleniyor'
                : row.record?.status === RECORD_STATUS.IMPL_RESULT_PENDING
                  ? 'OPEX atayacak'
                  : '—',
          kurulReady:
            row.record?.status === RECORD_STATUS.AWARD_POOL ||
            row.record?.status === RECORD_STATUS.CLOSED_AWARDED ||
            row.record?.status === RECORD_STATUS.CLOSED_THANKS
              ? 'Değerlendirmeye hazır'
              : 'Doğrulama bekleniyor',
          lastUpdate: formatDateDMY(
            row.tracking?.lastUpdateAt?.slice?.(0, 10) ||
              row.updatedAt?.slice?.(0, 10) ||
              row.realization?.lastUpdateDate ||
              ''
          ),
          raw: row,
        }
      }),
    [rows]
  )

  const facilities = useMemo(() => {
    const set = new Set(tableRows.map((r) => r.facility).filter((f) => f && f !== '—'))
    return [...set].sort((a, b) => a.localeCompare(b, 'tr'))
  }, [tableRows])

  const filteredRows = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('tr-TR')
    return tableRows.filter((row) => {
      if (!isOpexUser) {
        const managerEmail = normalizeEmail(row.raw?.record?.relatedDepartmentManagerEmail)
        const leadEmail = normalizeEmail(row.raw?.plan?.leaderEmail)
        if (managerEmail !== userEmail && leadEmail !== userEmail) return false
      }
      if (facilityFilter && row.facility !== facilityFilter) return false
      if (statusFilter && row.status !== statusFilter) return false
      if (!q) return true
      const hay = [
        row.recordNumber,
        row.uygulamaNo,
        row.topic,
        row.owner,
        row.leader,
        row.department,
        row.summary,
        row.actualBenefitArea,
      ]
        .join(' ')
        .toLocaleLowerCase('tr-TR')
      return hay.includes(q)
    })
  }, [tableRows, facilityFilter, statusFilter, search, isOpexUser, userEmail])

  const selected = useMemo(
    () =>
      filteredRows.find((r) => r.id === selectedId)?.raw ||
      rows.find((r) => r.id === selectedId) ||
      null,
    [filteredRows, rows, selectedId]
  )

  useEffect(() => {
    if (!selected) return
    const resolved = resolveAppState(selected, selected.record)
    const r = selected.realization || {}
    setAppState(resolved)
    setStatusNote(selected.tracking?.statusNote || '')
    setCompletionDate(selected.tracking?.completionDate || r.lastUpdateDate || '')
    setOutcome({
      leaderResult: r.leaderResult || r.actualResult || '',
      leaderKpi: r.leaderKpi || '',
      leaderNumeric: r.leaderNumeric || '',
      leaderQualitative: r.leaderQualitative || '',
      leaderAchievement: r.leaderAchievement || '',
      leaderEvidence: r.leaderEvidence || r.evidence || '',
      leaderCost: r.leaderCost ?? r.actualCost ?? '',
      leaderGain: r.leaderGain ?? r.verifiedGain ?? '',
    })
    const choice =
      selected.opexVerification?.verificationChoice ||
      r.verificationChoice ||
      VERIFICATION_CHOICE.SAME
    setVerifyForm({
      verificationChoice: choice,
      revisionReason: selected.opexVerification?.revisionReason || r.revisionReason || '',
      actualBenefitArea: r.actualBenefitArea || '',
      verifier: r.verifier || selected.opexVerification?.verifier || 'OPEX',
      verifyOwner: r.verifyOwner || selected.opexVerification?.verifyOwner || '',
      verifyMethod: r.verifyMethod || selected.opexVerification?.verifyMethod || '',
      verifyPeriod: r.verifyPeriod || selected.opexVerification?.verifyPeriod || '',
      verifyDate:
        r.verifyDate || selected.opexVerification?.verifyDate || isoToday(),
      evidence: r.evidence || selected.opexVerification?.evidence || r.leaderEvidence || '',
      note: selected.opexVerification?.note || r.assignmentNote || '',
      verifiedKpi: r.verifiedKpi || r.leaderKpi || '',
      verifiedNumeric: r.verifiedNumeric || r.leaderNumeric || '',
      verifiedQualitative: r.verifiedQualitative || r.leaderQualitative || '',
      verifyAchievement: r.verifyAchievement || r.leaderAchievement || '',
      actualCost: r.actualCost ?? r.leaderCost ?? '',
      verifiedGain: r.verifiedGain ?? r.leaderGain ?? '',
    })
    setSavedResultAttachments(
      normalizeResultAttachments(
        selected.opexVerification?.resultAttachments || r.resultAttachments
      )
    )
    setPendingResultFiles([])
    setAttachmentError('')
    setClientError('')
    setDetailTab('actions')
    // selected?.id: form yalnızca kayıt değişince reset; her selected referansında değil
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id])

  useEffect(() => {
    let mounted = true
    ;(async () => {
      const recordId = selected?.recordId || selected?.record?.id
      if (!recordId) {
        setHistoryRows([])
        return
      }
      setHistoryLoading(true)
      try {
        const list = await fetchRecordHistory(recordId)
        if (mounted) setHistoryRows(list)
      } finally {
        if (mounted) setHistoryLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [selected?.id, selected?.recordId, selected?.record?.id, fetchRecordHistory, selected?.updatedAt])

  const stats = useMemo(() => {
    const total = filteredRows.length
    const notStarted = filteredRows.filter((r) => r.appState === APP_STATE.NOT_STARTED).length
    const running = filteredRows.filter((r) => r.appState === APP_STATE.IN_PROGRESS).length
    const delayed = filteredRows.filter((r) => r.appStateLabel === 'Gecikti').length
    const verify = filteredRows.filter(
      (r) => r.status === RECORD_STATUS.IMPL_RESULT_PENDING
    ).length
    return { total, delayed, running, verify, notStarted }
  }, [filteredRows])

  const relatedManagerEmail = normalizeEmail(
    selected?.record?.relatedDepartmentManagerEmail || selected?.record?.assignee?.email
  )
  const leaderEmail = normalizeEmail(selected?.plan?.leaderEmail)
  const verificationState =
    selected?.opexVerification?.result === 'verified' ||
    selected?.record?.status === RECORD_STATUS.AWARD_POOL ||
    String(selected?.record?.status || '').startsWith('closed_')
      ? 'closed'
      : selected?.opexVerification?.result === 'returned' ||
          selected?.record?.status === RECORD_STATUS.IMPL_CORRECTION
        ? 'returned'
        : selected?.record?.status === RECORD_STATUS.IMPL_RESULT_PENDING
          ? 'pending'
          : 'none'

  const canEditActions =
    Boolean(selected) &&
    (userEmail === relatedManagerEmail || userEmail === leaderEmail) &&
    verificationState !== 'closed' &&
    verificationState !== 'pending'

  const canEditVerify =
    Boolean(selected) && isOpexUser && verificationState === 'pending'

  const patchOutcome = (patch) => setOutcome((prev) => ({ ...prev, ...patch }))

  const handleSaveTracking = async () => {
    if (!selected || !canEditActions) return
    setClientError('')
    if (appState === APP_STATE.COMPLETED) {
      if (!completionDate) {
        setClientError('Tamamlanma tarihi zorunludur.')
        return
      }
      if (!outcome.leaderResult.trim()) {
        setClientError('Yapılan çalışmanın özeti zorunludur.')
        return
      }
      const hasNumeric = Boolean(String(outcome.leaderNumeric || '').trim())
      const hasQual = Boolean(String(outcome.leaderQualitative || '').trim())
      if (!hasNumeric && !hasQual) {
        setClientError('Sayısal veya nitel sonuçtan en az birini doldurun.')
        return
      }
      if (hasNumeric && !String(outcome.leaderKpi || '').trim()) {
        setClientError('Gerçekleşen değer için KPI girin.')
        return
      }
      if (
        hasQual &&
        (!outcome.leaderAchievement || !String(outcome.leaderEvidence || '').trim())
      ) {
        setClientError('Nitel sonuç için gerçekleşme durumu ve kanıt girin.')
        return
      }
    }
    const nextStatus = deriveRecordStatusFromAppState(appState, {
      verificationState: appState === APP_STATE.COMPLETED ? 'pending' : 'none',
    })
    // When completing again after return, clear returned state via pending
    const realizationPayload = {
      ...(selected.realization || {}),
      leaderResult: outcome.leaderResult.trim(),
      actualResult: outcome.leaderResult.trim(),
      leaderKpi: outcome.leaderKpi,
      leaderNumeric: outcome.leaderNumeric,
      leaderQualitative: outcome.leaderQualitative,
      leaderAchievement: outcome.leaderAchievement,
      leaderEvidence: outcome.leaderEvidence,
      evidence: outcome.leaderEvidence,
      leaderCost: amountInputToNumberOrNull(outcome.leaderCost),
      leaderGain: amountInputToNumberOrNull(outcome.leaderGain),
      lastUpdateDate: completionDate || isoToday(),
    }
    if (appState === APP_STATE.COMPLETED) {
      // reset verification choice when re-submitting after return
      delete realizationPayload.verificationChoice
    }

    setSaving(true)
    try {
      await updateImplementationTracking(
        selected.id,
        {
          tracking: {
            appState,
            statusNote: statusNote.trim(),
            completionDate: completionDate || '',
            lastUpdateBy: userEmail,
          },
          realization: realizationPayload,
          opexVerification:
            appState === APP_STATE.COMPLETED
              ? {
                  ...(selected.opexVerification || {}),
                  result: 'pending',
                  note: '',
                }
              : selected.opexVerification?.result === 'returned'
                ? selected.opexVerification
                : selected.opexVerification || null,
          historyMessage:
            appState === APP_STATE.COMPLETED
              ? 'Sonuç OPEX doğrulamasına iletildi'
              : `Uygulama takibi kaydedildi (${getAppStateLabel(appState)})`,
        },
        {
          recordStatus: nextStatus,
          actor: userEmail,
          requireAssigneeMatch: true,
        }
      )
      await load()
    } catch (_) {
      /* toast */
    } finally {
      setSaving(false)
    }
  }

  const applyVerifyChoice = (choice) => {
    setVerifyForm((prev) => {
      if (choice === VERIFICATION_CHOICE.SAME) {
        return {
          ...prev,
          verificationChoice: choice,
          verifiedKpi: outcome.leaderKpi || '',
          verifiedNumeric: outcome.leaderNumeric || '',
          verifiedQualitative: outcome.leaderQualitative || '',
          verifyAchievement: outcome.leaderAchievement || '',
          actualCost: outcome.leaderCost ?? '',
          verifiedGain: outcome.leaderGain ?? '',
          evidence: outcome.leaderEvidence || prev.evidence,
        }
      }
      return { ...prev, verificationChoice: choice }
    })
  }

  const handleVerify = async (mode) => {
    if (!selected || !canEditVerify) return
    setClientError('')
    if (mode === 'return') {
      if (!verifyForm.note.trim()) {
        setClientError('Düzeltme açıklamasını yazın.')
        return
      }
      setSaving(true)
      try {
        await verifyImplementation(
          selected.id,
          { ...verifyForm, note: verifyForm.note.trim() },
          { approve: false, actor: currentUser }
        )
        await load()
      } catch (_) {
        /* toast */
      } finally {
        setSaving(false)
      }
      return
    }

    if (!verifyForm.actualBenefitArea) {
      setClientError('Gerçekleşen fayda alanını seçin.')
      return
    }
    if (
      verifyForm.verificationChoice === VERIFICATION_CHOICE.REVISE &&
      !verifyForm.revisionReason.trim()
    ) {
      setClientError('Revizyon açıklamasını girin.')
      return
    }
    if (!verifyForm.verifyOwner.trim() || !verifyForm.verifyMethod || !verifyForm.verifyDate) {
      setClientError('Doğrulama sorumlusu, yöntem ve tarihi doldurun.')
      return
    }
    if (!String(verifyForm.evidence || '').trim()) {
      setClientError('Kanıt / ölçüm açıklamasını girin.')
      return
    }
    const hasNum = Boolean(String(verifyForm.verifiedNumeric || '').trim())
    const hasQual = Boolean(String(verifyForm.verifiedQualitative || '').trim())
    if (!hasNum && !hasQual) {
      setClientError('Doğrulanan sayısal veya nitel sonuçtan en az biri gerekli.')
      return
    }

    setSaving(true)
    try {
      let uploaded = []
      if (pendingResultFiles.length) {
        uploaded = await uploadResultAttachments(selected.id, pendingResultFiles)
      }
      const resultAttachments = [...savedResultAttachments, ...uploaded].slice(
        0,
        ATTACHMENT_MAX_FILES
      )
      const realizationMerge = {
        ...(selected.realization || {}),
        actualBenefitArea: verifyForm.actualBenefitArea,
        verificationChoice: verifyForm.verificationChoice,
        revisionReason:
          verifyForm.verificationChoice === VERIFICATION_CHOICE.REVISE
            ? verifyForm.revisionReason.trim()
            : '',
        verifiedKpi: verifyForm.verifiedKpi,
        verifiedNumeric: verifyForm.verifiedNumeric,
        verifiedQualitative: verifyForm.verifiedQualitative,
        verifyAchievement: verifyForm.verifyAchievement,
        actualCost: amountInputToNumberOrNull(verifyForm.actualCost),
        verifiedGain: amountInputToNumberOrNull(verifyForm.verifiedGain),
        verifier: verifyForm.verifier,
        verifyOwner: verifyForm.verifyOwner.trim(),
        verifyMethod: verifyForm.verifyMethod,
        verifyPeriod: verifyForm.verifyPeriod,
        verifyDate: verifyForm.verifyDate,
        evidence: verifyForm.evidence.trim(),
        assignmentNote: verifyForm.note.trim(),
        resultAttachments,
        actualResult: [verifyForm.verifiedNumeric, verifyForm.verifiedQualitative]
          .filter(Boolean)
          .join(' • '),
      }
      await verifyImplementation(
        selected.id,
        {
          verificationChoice: verifyForm.verificationChoice,
          revisionReason: realizationMerge.revisionReason,
          note: verifyForm.note.trim(),
          verifier: verifyForm.verifier,
          verifyOwner: verifyForm.verifyOwner.trim(),
          verifyMethod: verifyForm.verifyMethod,
          verifyPeriod: verifyForm.verifyPeriod,
          verifyDate: verifyForm.verifyDate,
          evidence: verifyForm.evidence.trim(),
          resultAttachments,
        },
        {
          approve: true,
          actor: currentUser,
          realization: realizationMerge,
          historyMessage:
            verifyForm.verificationChoice === VERIFICATION_CHOICE.SAME
              ? 'OPEX lider sonucunu olduğu gibi doğruladı'
              : `OPEX sonucu revize ederek doğruladı: ${realizationMerge.revisionReason}`,
        }
      )
      setPendingResultFiles([])
      setSavedResultAttachments(resultAttachments)
      setAttachmentError('')
      await load()
    } catch (_) {
      /* toast */
    } finally {
      setSaving(false)
    }
  }

  const benefit = selected?.benefit || {}
  const showOutcome = appState === APP_STATE.COMPLETED
  const verifyReadOnly = verifyForm.verificationChoice === VERIFICATION_CHOICE.SAME

  useEffect(() => {
    if (!filteredRows.length) {
      if (selectedId) setSelectedId('')
      return
    }
    if (!filteredRows.some((r) => r.id === selectedId)) {
      setSelectedId(filteredRows[0].id)
    }
  }, [filteredRows, selectedId])

  useEffect(() => {
    if (!isOpexUser && detailTab === 'result') setDetailTab('actions')
  }, [isOpexUser, detailTab])

  const handleGateSubmit = async (e) => {
    e.preventDefault()
    setGateBusy(true)
    setGateMessage('')
    try {
      await requestTrackingAccess(gateEmail)
      setGateMessage(
        'E-posta sistemde kayıtlıysa giriş bağlantısı gönderildi. Gelen kutunuzu kontrol edin.'
      )
    } finally {
      setGateBusy(false)
    }
  }

  const handleEmailLogout = () => {
    clearTrackingSession()
    setEmailSession(null)
    setRows([])
    setSelectedId('')
    setGateMessage('')
  }

  if (claimingAccess) {
    return (
      <div className="odul-opex-takip">
        <div className="access-gate">
          <p>Giriş doğrulanıyor…</p>
        </div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="odul-opex-takip">
        <header className="odul-opex-takip-header">
          <div>
            <div className="eyebrow">BONNA • Ödül Öneri</div>
            <h1>Uygulama Takibi</h1>
          </div>
        </header>
        <div className="access-gate">
          <h2>E-posta ile giriş</h2>
          <p>
            Uygulama lideri veya bölüm yöneticisi e-posta adresinizi girin. Size atanmış
            uygulamaları görmek için bağlantı gönderilir. OPEX kullanıcıları kurumsal
            hesaplarıyla{' '}
            <button type="button" className="linkish" onClick={() => navigate('/login')}>
              giriş
            </button>{' '}
            yapmalıdır.
          </p>
          <form onSubmit={handleGateSubmit} className="access-gate-form">
            <label>
              E-posta
              <input
                type="email"
                required
                autoComplete="email"
                value={gateEmail}
                onChange={(e) => setGateEmail(e.target.value)}
                placeholder="ornek@bonna.com.tr"
              />
            </label>
            <button type="submit" className="save" disabled={gateBusy}>
              {gateBusy ? 'Gönderiliyor…' : 'Giriş bağlantısı gönder'}
            </button>
          </form>
          {gateMessage && <div className="access-gate-msg">{gateMessage}</div>}
        </div>
      </div>
    )
  }

  return (
    <div className="odul-opex-takip">
      <header className="odul-opex-takip-header">
        <div>
          <div className="eyebrow">BONNA • {isOpexUser ? 'OPEX' : 'Ödül Öneri'}</div>
          <h1>Öneri Uygulama Takibi</h1>
        </div>
        <div className="meta">
          <div>
            {isOpexUser
              ? `OPEX: ${userEmail}`
              : accessViaEmailSession
                ? `E-posta girişi: ${userEmail}`
                : `Giriş: ${userEmail}`}
          </div>
          <div>Kayıt: {filteredRows.length}</div>
          {accessViaEmailSession && (
            <button type="button" className="header-logout" onClick={handleEmailLogout}>
              Çıkış
            </button>
          )}
        </div>
      </header>

      <div className="main">
        <section className="toolbar">
          <label>
            Tesis
            <select value={facilityFilter} onChange={(e) => setFacilityFilter(e.target.value)}>
              <option value="">Tüm tesisler</option>
              {facilities.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label>
            Durum
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tüm durumlar</option>
              {Object.entries(RECORD_STATUS_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Ara
            <input
              type="search"
              placeholder="Öneri no, kişi veya konu"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 220 }}
            />
          </label>
          <div className="push">
            {isOpexUser && (
              <RouterLink to="/odul-oneri/kurul-toplanti">Kurul Toplantısı</RouterLink>
            )}
            <button type="button" onClick={load}>
              Yenile
            </button>
          </div>
        </section>

        <section className="stats">
          <div>
            <b>{stats.total}</b>
            <span>Kabul edilen öneri</span>
          </div>
          <div>
            <b>{stats.notStarted}</b>
            <span>Başlamayan</span>
          </div>
          <div>
            <b>{stats.running}</b>
            <span>Uygulaması süren</span>
          </div>
          <div>
            <b>{stats.delayed}</b>
            <span>Geciken</span>
          </div>
          <div>
            <b>{stats.verify}</b>
            <span>Doğrulama / kapanış</span>
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <h2>Uygulama Takibindeki Kabul Edilmiş Öneriler</h2>
            <div className="legend">
              <span className="source opex">OPEX Ön İnceleme: Geçti</span>
              <span className="source">Bölüm yöneticisi: Kabul</span>
            </div>
          </div>
          <div className="table-wrap">
            {loading && <div className="empty">Yükleniyor…</div>}
            {!loading && filteredRows.length === 0 && (
              <div className="empty">
                {isOpexUser
                  ? 'Uygulama kaydı yok.'
                  : 'Size atanmış (lider veya bölüm yöneticisi) açık uygulama bulunamadı.'}
              </div>
            )}
            {!loading && filteredRows.length > 0 && (
              <table className="master">
                <thead>
                  <tr>
                    <th>Öneri / Uygulama No</th>
                    <th>Tesis</th>
                    <th>Öneri Detayları</th>
                    <th>Öneri Konusu</th>
                    <th>Öneri Sahibi</th>
                    <th>İlgili Bölüm</th>
                    <th>Uygulama Yolu</th>
                    <th>Uygulama Sahibi</th>
                    <th>Ana Termin</th>
                    <th>Uygulama Özeti</th>
                    <th>Uygulama Genel Durumu</th>
                    <th>Son Durum</th>
                    <th>Gerçekleşen Fayda Alanı</th>
                    <th>OPEX Doğrulama</th>
                    <th>Kurul Değerlendirmesi</th>
                    <th>Son Güncelleme</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((row) => (
                    <tr
                      key={row.id}
                      className={row.id === selectedId ? 'selected' : ''}
                      onClick={() => setSelectedId(row.id)}
                    >
                      <td>
                        <strong>
                          {row.recordNumber}
                          <br />
                          <small>{row.uygulamaNo}</small>
                        </strong>
                      </td>
                      <td>{row.facility}</td>
                      <td>
                        <button
                          type="button"
                          className="detail-btn"
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedId(row.id)
                            setProposalOpen(true)
                          }}
                        >
                          Detayı Gör
                        </button>
                      </td>
                      <td>{row.topic}</td>
                      <td>{row.owner}</td>
                      <td>{row.department}</td>
                      <td>
                        <span className="pill ok">{row.method || '—'}</span>
                      </td>
                      <td>{row.uygulamaSahibi}</td>
                      <td>{row.due}</td>
                      <td>{row.summary}</td>
                      <td>
                        <span className={statusPillClass(row.appStateLabel)}>
                          {row.appStateLabel}
                        </span>
                      </td>
                      <td>
                        <span className={statusPillClass(row.status)}>{row.statusLabel}</span>
                      </td>
                      <td>
                        <span className="source opex">{row.actualBenefitArea}</span>
                      </td>
                      <td>
                        <span className="source opex">{row.opexVerify}</span>
                      </td>
                      <td>
                        <span
                          className={
                            row.kurulReady === 'Değerlendirmeye hazır' ? 'pill ok' : 'pill wait'
                          }
                        >
                          {row.kurulReady}
                        </span>
                      </td>
                      <td>{row.lastUpdate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {selected && (
          <section className="detail">
            <aside className="box summary-sidebar">
              <div className="sidebar-head">
                <span className="label">Seçili öneri</span>
                <h3>{selected.recordNumber || selected.record?.recordNumber}</h3>
                <small>{selected.uygulamaNo}</small>
                <div style={{ marginTop: 8 }}>
                  <span
                    className={statusPillClass(
                      displayAppStateLabel(
                        resolveAppState(selected, selected.record),
                        selected.plan?.dueDate
                      )
                    )}
                  >
                    {getRecordStatusLabel(selected.record?.status)}
                  </span>
                </div>
              </div>
              <div className="sidebar-block">
                <div className="summary-row">
                  <span>Öneri sahibi</span>
                  <b>{selected.record?.applicant?.fullName || selected.applicantName || '—'}</b>
                </div>
                <div className="summary-row">
                  <span>Tesis / bölüm</span>
                  <b>
                    {(selected.facility || selected.record?.applicant?.location || '—') +
                      ' / ' +
                      (selected.record?.relatedDepartmentName ||
                        selected.relatedDepartmentName ||
                        '—')}
                  </b>
                </div>
                <div className="summary-row">
                  <span>Öneri konusu</span>
                  <b>{selected.topic || selected.record?.topic || '—'}</b>
                </div>
              </div>
              <div className="sidebar-block">
                <h4>Uygulama özeti</h4>
                <p>{selected.plan?.workSummary || 'Henüz tanımlanmadı.'}</p>
                <div className="summary-row">
                  <span>Uygulama yolu</span>
                  <b>{getImplementationMethodLabel(selected.plan?.method || selected.method)}</b>
                </div>
                <div className="summary-row">
                  <span>Bölüm yöneticisi</span>
                  <b>{selected.record?.relatedDepartmentManagerName || '—'}</b>
                </div>
                <div className="summary-row">
                  <span>Uygulama lideri</span>
                  <b>{selected.plan?.leaderName || '—'}</b>
                </div>
                <div className="summary-row">
                  <span>Ana termin</span>
                  <b>{formatDateDMY(selected.plan?.dueDate)}</b>
                </div>
              </div>
              <div className="sidebar-block target-block">
                <h4>Hedef ve beklenen fayda</h4>
                <p>{benefit.qualitativeNote || benefit.kpi || '—'}</p>
                <div className="summary-row">
                  <span>Başarı KPI’ı</span>
                  <b>{benefit.kpi || '—'}</b>
                </div>
                <div className="target-pair">
                  <div className="plan-item">
                    <span className="label">Mevcut</span>
                    {benefit.baseline || '—'}
                  </div>
                  <div className="plan-item">
                    <span className="label">Hedef</span>
                    {benefit.target || '—'}
                  </div>
                </div>
              </div>
              <details className="sidebar-extra">
                <summary>Diğer uygulama bilgileri</summary>
                <div className="summary-row">
                  <span>Başlangıç</span>
                  <b>{formatDateDMY(selected.plan?.startDate)}</b>
                </div>
                <div className="summary-row">
                  <span>Ekip</span>
                  <b>{selected.plan?.team || '—'}</b>
                </div>
                <div className="summary-row">
                  <span>Tahmini maliyet</span>
                  <b>{formatEur(benefit.estimatedCost)}</b>
                </div>
                <div className="summary-row">
                  <span>Tahmini kazanç</span>
                  <b>{formatEur(benefit.estimatedGain)}</b>
                </div>
                <div className="summary-row">
                  <span>Gerçekleşen fayda alanı</span>
                  <b>{selected.realization?.actualBenefitArea || '—'}</b>
                </div>
              </details>
            </aside>

            <section className="box work-panel">
              <div className="detail-tabs" aria-label="İşlem bölümleri">
                <button
                  type="button"
                  className={`tab${detailTab === 'actions' ? ' active' : ''}`}
                  onClick={() => setDetailTab('actions')}
                >
                  <b>Uygulama Takibi</b>
                  <small>Uygulama lideri / Bölüm yöneticisi</small>
                </button>
                {isOpexUser && (
                  <button
                    type="button"
                    className={`tab${detailTab === 'result' ? ' active' : ''}`}
                    onClick={() => setDetailTab('result')}
                  >
                    <b>Sonuç ve Doğrulama</b>
                    <small>OPEX</small>
                  </button>
                )}
                <button
                  type="button"
                  className={`tab${detailTab === 'history' ? ' active' : ''}`}
                  onClick={() => setDetailTab('history')}
                >
                  <b>Geçmiş</b>
                  <small>İşlem kayıtları</small>
                </button>
              </div>

              {detailTab === 'actions' && (
                <div className="tabpane">
                  <h3 className="pane-title">Uygulama Takibi</h3>
                  {!canEditActions && (
                    <div className="role-note">
                      {verificationState === 'pending'
                        ? 'Sonuç OPEX doğrulamasında. Düzenleme için OPEX’in düzeltme istemesi gerekir.'
                        : verificationState === 'closed'
                          ? 'Doğrulama tamamlandı. Kayıt kapandı.'
                          : 'Bu bölümü yalnızca ilgili bölüm yöneticisi veya uygulama lideri güncelleyebilir.'}
                    </div>
                  )}
                  {verificationState === 'returned' && (
                    <div className="alert">
                      <b>OPEX düzeltme istedi</b>
                      <br />
                      {selected.opexVerification?.note || '—'}
                    </div>
                  )}
                  <fieldset className="edit" disabled={!canEditActions}>
                    <p className="section-caption">Durum ve takip notu</p>
                    <div className="tracking-grid">
                      <label>
                        <span className="label">Uygulama Genel Durumu</span>
                        <select
                          value={appState}
                          onChange={(e) => setAppState(e.target.value)}
                        >
                          {APP_STATE_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                              {getAppStateLabel(s)}
                            </option>
                          ))}
                        </select>
                        <small>Gecikti durumu ana termine göre otomatik görünür.</small>
                      </label>
                      <label>
                        <span className="label">Uygulama tamamlanma tarihi</span>
                        <input
                          type="date"
                          value={completionDate}
                          onChange={(e) => setCompletionDate(e.target.value)}
                          disabled={!showOutcome}
                        />
                      </label>
                      <label className="full">
                        <span className="label">Uygulama Lideri Notu</span>
                        <textarea
                          value={statusNote}
                          onChange={(e) => setStatusNote(e.target.value)}
                        />
                      </label>
                    </div>

                    {showOutcome && (
                      <section className="leader-result">
                        <p className="section-caption">Uygulama sonucu</p>
                        <p className="result-help">
                          Yapılan çalışmayı açıklayın. Sayısal veya nitel sonuçtan en az birini
                          doldurun; ikisini birlikte de girebilirsiniz.
                        </p>
                        <label className="full">
                          <span className="label">Yapılan çalışmanın özeti</span>
                          <textarea
                            value={outcome.leaderResult}
                            onChange={(e) => patchOutcome({ leaderResult: e.target.value })}
                          />
                        </label>
                        <div className="outcome-grid">
                          <div className="outcome-card">
                            <h4>Sayısal sonuç</h4>
                            <p>Ölçüm veya parasal kazanç varsa doldurun.</p>
                            <div className="kpi-input-pair">
                              <label>
                                <span className="label">KPI</span>
                                <input
                                  value={outcome.leaderKpi}
                                  onChange={(e) => patchOutcome({ leaderKpi: e.target.value })}
                                  placeholder="Örn. Taşıma süresi"
                                />
                              </label>
                              <label>
                                <span className="label">Gerçekleşen değer</span>
                                <input
                                  value={outcome.leaderNumeric}
                                  onChange={(e) =>
                                    patchOutcome({ leaderNumeric: e.target.value })
                                  }
                                  placeholder="Örn. 9 dk/taşıma"
                                />
                              </label>
                            </div>
                            <label>
                              <span className="label">Fiili maliyet (€) — varsa</span>
                              <MoneyInput
                                value={outcome.leaderCost}
                                onChange={(v) => patchOutcome({ leaderCost: v })}
                              />
                            </label>
                            <label>
                              <span className="label">Yıllık kazanç (€) — varsa</span>
                              <MoneyInput
                                value={outcome.leaderGain}
                                onChange={(v) => patchOutcome({ leaderGain: v })}
                              />
                            </label>
                          </div>
                          <div className="outcome-card">
                            <h4>Nitel sonuç</h4>
                            <p>Sağlanan iyileşmeyi ve gözleminizi açıklayın.</p>
                            <label className="full">
                              <span className="label">Gerçekleşen iyileşme / nitel fayda</span>
                              <textarea
                                value={outcome.leaderQualitative}
                                onChange={(e) =>
                                  patchOutcome({ leaderQualitative: e.target.value })
                                }
                              />
                            </label>
                            <label>
                              <span className="label">
                                Hedeflenen iyileşmenin gerçekleşme durumu
                              </span>
                              <select
                                value={outcome.leaderAchievement}
                                onChange={(e) =>
                                  patchOutcome({ leaderAchievement: e.target.value })
                                }
                              >
                                <option value="">Seçin</option>
                                {ACHIEVEMENT_OPTIONS.map((o) => (
                                  <option key={o} value={o}>
                                    {o}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label className="full">
                              <span className="label">Başarı kanıtı / gözlem açıklaması</span>
                              <textarea
                                value={outcome.leaderEvidence}
                                onChange={(e) =>
                                  patchOutcome({ leaderEvidence: e.target.value })
                                }
                              />
                            </label>
                          </div>
                        </div>
                      </section>
                    )}
                  </fieldset>
                  {clientError && detailTab === 'actions' && (
                    <div className="alert">{clientError}</div>
                  )}
                  <div className="section-actions">
                    <button
                      type="button"
                      className="save"
                      disabled={saving || !canEditActions}
                      onClick={handleSaveTracking}
                    >
                      {verificationState === 'returned'
                        ? 'Düzelt ve Yeniden Doğrulamaya Gönder'
                        : 'Uygulama Takibini Kaydet'}
                    </button>
                  </div>
                </div>
              )}

              {detailTab === 'result' && (
                <div className="tabpane">
                  <h3 className="pane-title">Sonuç ve Doğrulama</h3>
                  {!canEditVerify && (
                    <div className="role-note">
                      {verificationState === 'closed'
                        ? 'Doğrulama tamamlandı; kayıt kapandı.'
                        : verificationState === 'returned'
                          ? 'Liderin düzeltip yeniden kaydetmesi bekleniyor.'
                          : isOpexUser
                            ? 'Uygulama liderinin sonucu Tamamlandı olarak kaydetmesi bekleniyor.'
                            : 'Bu bölümü yalnızca OPEX kullanıcıları düzenleyebilir.'}
                    </div>
                  )}
                  <section className="verify-section">
                    <div className="verify-heading">
                      <span>1</span>
                      <div>
                        <h4>Uygulama sonucu</h4>
                        <p>Liderin sonuçlarını inceleyin; gerekiyorsa revize edin.</p>
                      </div>
                    </div>
                    <p className="leader-summary">
                      {outcome.leaderResult ||
                        selected.realization?.leaderResult ||
                        selected.realization?.actualResult ||
                        'Henüz sonuç kaydedilmedi.'}
                    </p>
                    <fieldset className="edit" disabled={!canEditVerify}>
                      <label>
                        <span className="label">Doğrulama seçimi</span>
                        <select
                          value={verifyForm.verificationChoice}
                          onChange={(e) => applyVerifyChoice(e.target.value)}
                        >
                          {Object.entries(VERIFICATION_CHOICE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>
                              {label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="outcome-grid" style={{ marginTop: 14 }}>
                        <div className="outcome-card">
                          <h4>Sayısal sonuç</h4>
                          <div className="kpi-input-pair">
                            <label>
                              <span className="label">KPI</span>
                              <input
                                readOnly={verifyReadOnly || !canEditVerify}
                                value={verifyForm.verifiedKpi}
                                onChange={(e) =>
                                  setVerifyForm((p) => ({ ...p, verifiedKpi: e.target.value }))
                                }
                              />
                            </label>
                            <label>
                              <span className="label">Gerçekleşen değer</span>
                              <input
                                readOnly={verifyReadOnly || !canEditVerify}
                                value={verifyForm.verifiedNumeric}
                                onChange={(e) =>
                                  setVerifyForm((p) => ({
                                    ...p,
                                    verifiedNumeric: e.target.value,
                                  }))
                                }
                              />
                            </label>
                          </div>
                          <label>
                            <span className="label">Fiili maliyet (€) — varsa</span>
                            <MoneyInput
                              readOnly={verifyReadOnly || !canEditVerify}
                              value={verifyForm.actualCost}
                              onChange={(v) =>
                                setVerifyForm((p) => ({ ...p, actualCost: v }))
                              }
                            />
                          </label>
                          <label>
                            <span className="label">Yıllık kazanç (€) — varsa</span>
                            <MoneyInput
                              readOnly={verifyReadOnly || !canEditVerify}
                              value={verifyForm.verifiedGain}
                              onChange={(v) =>
                                setVerifyForm((p) => ({ ...p, verifiedGain: v }))
                              }
                            />
                          </label>
                        </div>
                        <div className="outcome-card">
                          <h4>Nitel sonuç</h4>
                          <label className="full">
                            <span className="label">Gerçekleşen iyileşme / nitel fayda</span>
                            <textarea
                              readOnly={verifyReadOnly || !canEditVerify}
                              value={verifyForm.verifiedQualitative}
                              onChange={(e) =>
                                setVerifyForm((p) => ({
                                  ...p,
                                  verifiedQualitative: e.target.value,
                                }))
                              }
                            />
                          </label>
                          <label>
                            <span className="label">Hedef gerçekleşme durumu</span>
                            <select
                              disabled={verifyReadOnly || !canEditVerify}
                              value={verifyForm.verifyAchievement}
                              onChange={(e) =>
                                setVerifyForm((p) => ({
                                  ...p,
                                  verifyAchievement: e.target.value,
                                }))
                              }
                            >
                              <option value="">Seçin</option>
                              {ACHIEVEMENT_OPTIONS.map((o) => (
                                <option key={o} value={o}>
                                  {o}
                                </option>
                              ))}
                            </select>
                          </label>
                        </div>
                      </div>
                      {verifyForm.verificationChoice === VERIFICATION_CHOICE.REVISE && (
                        <label className="full" style={{ marginTop: 10 }}>
                          <span className="label">Revizyon açıklaması</span>
                          <textarea
                            value={verifyForm.revisionReason}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, revisionReason: e.target.value }))
                            }
                          />
                        </label>
                      )}
                    </fieldset>
                    <details className="verify-fold">
                      <summary>Başlangıç hedefi ve liderin ilk sonuçları</summary>
                      <div className="verify-read-grid">
                        <div className="plan-item">
                          <span className="label">Mevcut</span>
                          {benefit.baseline || '—'}
                        </div>
                        <div className="plan-item">
                          <span className="label">Hedef</span>
                          {benefit.target || '—'}
                        </div>
                        <div className="plan-item">
                          <span className="label">Lider KPI</span>
                          {outcome.leaderKpi || '—'}
                        </div>
                        <div className="plan-item">
                          <span className="label">Lider gerçekleşen</span>
                          {outcome.leaderNumeric || '—'}
                        </div>
                        <div className="plan-item">
                          <span className="label">Lider nitel</span>
                          {outcome.leaderQualitative || '—'}
                        </div>
                        <div className="plan-item">
                          <span className="label">Lider maliyet</span>
                          {formatEur(outcome.leaderCost)}
                        </div>
                        <div className="plan-item">
                          <span className="label">Lider kazanç</span>
                          {formatEur(outcome.leaderGain)}
                        </div>
                      </div>
                    </details>
                  </section>

                  <section className="verify-section verification-info">
                    <div className="verify-heading">
                      <span>2</span>
                      <div>
                        <h4>Doğrulama bilgileri</h4>
                        <p>Kim, nasıl ve hangi kanıtla doğruladı?</p>
                      </div>
                    </div>
                    <fieldset className="edit" disabled={!canEditVerify}>
                      <div className="verification-grid">
                        <label className="full">
                          <span className="label">Gerçekleşen fayda alanı *</span>
                          <select
                            value={verifyForm.actualBenefitArea}
                            onChange={(e) =>
                              setVerifyForm((p) => ({
                                ...p,
                                actualBenefitArea: e.target.value,
                              }))
                            }
                          >
                            <option value="">Gerçekleşen fayda alanını seçin</option>
                            {ACTUAL_BENEFIT_AREAS.map((a) => (
                              <option key={a} value={a}>
                                {a}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span className="label">Doğrulama birimi</span>
                          <select
                            value={verifyForm.verifier}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, verifier: e.target.value }))
                            }
                          >
                            {OPEX_VERIFIER_UNITS.map((u) => (
                              <option key={u} value={u}>
                                {u}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span className="label">Doğrulama sorumlusu</span>
                          <input
                            value={verifyForm.verifyOwner}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, verifyOwner: e.target.value }))
                            }
                          />
                        </label>
                        <label>
                          <span className="label">Doğrulama yöntemi</span>
                          <select
                            value={verifyForm.verifyMethod}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, verifyMethod: e.target.value }))
                            }
                          >
                            <option value="">Seçin</option>
                            {OPEX_VERIFY_METHODS.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span className="label">Doğrulama dönemi</span>
                          <select
                            value={verifyForm.verifyPeriod}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, verifyPeriod: e.target.value }))
                            }
                          >
                            <option value="">Seçin</option>
                            {OPEX_VERIFY_PERIODS.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </label>
                        <label>
                          <span className="label">Doğrulama tarihi</span>
                          <input
                            type="date"
                            value={verifyForm.verifyDate}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, verifyDate: e.target.value }))
                            }
                          />
                        </label>
                        <label className="full">
                          <span className="label">Kanıt / ölçüm veya saha gözlemi</span>
                          <textarea
                            value={verifyForm.evidence}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, evidence: e.target.value }))
                            }
                          />
                        </label>
                        <div className="full result-attachments">
                          <span className="label">Sonuç ekleri (isteğe bağlı)</span>
                          <p className="attachment-help">
                            Doğrulama kanıtı fotoğraf veya dokümanları. Kurul toplantısında
                            «OPEX Doğrulaması / Ekler» sütununda görünür. En fazla{' '}
                            {ATTACHMENT_MAX_FILES} dosya.
                          </p>
                          {canEditVerify && (
                            <>
                              <label
                                className="attachment-button"
                                htmlFor="opex-result-attachment-input"
                              >
                                Dosya Seç
                              </label>
                              <input
                                id="opex-result-attachment-input"
                                type="file"
                                multiple
                                accept={ATTACHMENT_ACCEPT}
                                style={{ display: 'none' }}
                                onChange={(e) => {
                                  const room = Math.max(
                                    0,
                                    ATTACHMENT_MAX_FILES -
                                      savedResultAttachments.length -
                                      pendingResultFiles.length
                                  )
                                  const list = Array.from(e.target.files || []).slice(0, room)
                                  for (const file of list) {
                                    const err = validateAttachment(file)
                                    if (err) {
                                      setAttachmentError(err)
                                      e.target.value = ''
                                      return
                                    }
                                  }
                                  setAttachmentError('')
                                  setPendingResultFiles((prev) => [...prev, ...list])
                                  e.target.value = ''
                                }}
                              />
                            </>
                          )}
                          <ul className="attachment-list" aria-live="polite">
                            {savedResultAttachments.map((a) => (
                              <li key={a.id || a.url}>
                                <a
                                  href={a.url || a.dataUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {a.name}
                                  {a.size ? (
                                    <span className="attachment-size">
                                      {' '}
                                      ({formatFileSize(a.size)})
                                    </span>
                                  ) : null}
                                </a>
                                {canEditVerify && (
                                  <button
                                    type="button"
                                    className="attachment-remove"
                                    onClick={() =>
                                      setSavedResultAttachments((prev) =>
                                        prev.filter((x) => x !== a)
                                      )
                                    }
                                  >
                                    Kaldır
                                  </button>
                                )}
                              </li>
                            ))}
                            {pendingResultFiles.map((f) => (
                              <li key={`pending-${f.name}-${f.size}-${f.lastModified}`}>
                                <span className="attachment-name">
                                  {f.name}{' '}
                                  <span className="attachment-size">
                                    ({formatFileSize(f.size)}) — yeni
                                  </span>
                                </span>
                                <button
                                  type="button"
                                  className="attachment-remove"
                                  onClick={() =>
                                    setPendingResultFiles((prev) =>
                                      prev.filter((x) => x !== f)
                                    )
                                  }
                                >
                                  Kaldır
                                </button>
                              </li>
                            ))}
                            {!savedResultAttachments.length &&
                              !pendingResultFiles.length && (
                                <li className="attachment-empty">Ek dosya yok.</li>
                              )}
                          </ul>
                          {attachmentError && (
                            <div className="alert" style={{ marginTop: 8 }}>
                              {attachmentError}
                            </div>
                          )}
                        </div>
                        <label className="full">
                          <span className="label">OPEX notu (isteğe bağlı)</span>
                          <textarea
                            value={verifyForm.note}
                            onChange={(e) =>
                              setVerifyForm((p) => ({ ...p, note: e.target.value }))
                            }
                          />
                        </label>
                      </div>
                    </fieldset>
                  </section>

                  {clientError && detailTab === 'result' && (
                    <div className="alert">{clientError}</div>
                  )}
                  <div className="section-actions verify-save">
                    <button
                      type="button"
                      className="secondary"
                      disabled={saving || !canEditVerify}
                      onClick={() => handleVerify('return')}
                    >
                      Düzeltme İste
                    </button>
                    <button
                      type="button"
                      className="save"
                      disabled={saving || !canEditVerify}
                      onClick={() => handleVerify('close')}
                    >
                      Doğrula ve Kaydet
                    </button>
                  </div>
                </div>
              )}

              {detailTab === 'history' && (
                <div className="tabpane">
                  <h3 className="pane-title">Uygulama Geçmişi</h3>
                  {historyLoading && <p className="empty">Geçmiş yükleniyor…</p>}
                  {!historyLoading && historyRows.length === 0 && (
                    <p className="empty">Henüz işlem kaydı yok.</p>
                  )}
                  {!historyLoading && historyRows.length > 0 && (
                    <div className="timeline">
                      {historyRows.map((ev) => {
                        let msg = ev.payload?.message || ''
                        if (
                          ev.type === 'board_decision' &&
                          /puan\s*null/i.test(msg)
                        ) {
                          const score = resolveBoardGroupScore(
                            selected?.boardDecision || selected?.record?.boardDecision,
                            selected?.realization?.actualBenefitArea ||
                              selected?.topic ||
                              selected?.record?.topic
                          )
                          if (score != null) {
                            msg = msg.replace(
                              /puan\s*null/i,
                              `puan ${formatWeightedScore(score)}`
                            )
                          }
                        }
                        return (
                          <p className="event" key={ev.id}>
                            <b>{getHistoryTypeLabel(ev.type)}</b>
                            {msg ? <span className="event-msg">{msg}</span> : null}
                            <small>
                              {formatHistoryWhen(ev.createdAt)}
                              {ev.actor ? ` • ${ev.actor}` : ''}
                            </small>
                          </p>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </section>
          </section>
        )}
      </div>

      {proposalOpen && selected && (
        <div
          className="proposal-dialog-backdrop"
          onClick={() => setProposalOpen(false)}
          role="presentation"
        >
          <div
            className="proposal-dialog"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="dialog-head">
              <h2>Öneri Detayları</h2>
              <button type="button" onClick={() => setProposalOpen(false)} aria-label="Kapat">
                ×
              </button>
            </div>
            <div className="dialog-body">
              <div className="verify-read-grid">
                <div className="plan-item">
                  <span className="label">Öneri numarası</span>
                  {selected.recordNumber || selected.record?.recordNumber}
                </div>
                <div className="plan-item">
                  <span className="label">Öneri sahibi</span>
                  {selected.record?.applicant?.fullName || '—'}
                </div>
                <div className="plan-item">
                  <span className="label">Tesis / Bölüm</span>
                  {(selected.facility || selected.record?.applicant?.location || '—') +
                    ' / ' +
                    (selected.record?.relatedDepartmentName || '—')}
                </div>
                <div className="plan-item">
                  <span className="label">Öneri konusu</span>
                  {selected.topic || selected.record?.topic || '—'}
                </div>
              </div>
              <p className="section-caption">Mevcut durum / problem</p>
              <div className="proposal-text">
                {selected.currentState || selected.record?.currentState || '—'}
              </div>
              <p className="section-caption">Önerilen iyileştirme / öneri fikri</p>
              <div className="proposal-text">
                {selected.proposedState || selected.record?.proposedState || '—'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default OpexUygulamaTakip
